import { IUseCase, LoginUseCaseRequest, LoginUseCaseResponse, LogoutUseCaseRequest } from './interfaces'
import { IAuthRepository } from '@/core/domain/repositories/IAuthRepository'
import { AuthSession } from '@/core/domain/entities/User'
import { Result, createOk, createErr, isOk } from '@/shared/utils/Result'
import { UnauthorizedError, ValidationError } from '@/shared/errors'

export class LoginUseCase implements IUseCase<LoginUseCaseRequest, LoginUseCaseResponse> {
  constructor(private readonly authRepository: IAuthRepository) {}

  async execute(request: LoginUseCaseRequest): Promise<Result<LoginUseCaseResponse, Error>> {
    try {
      // Validate input
      if (!request.token || request.token.trim() === '') {
        return createErr(new ValidationError('Token is required'))
      }

      // Check authentication with the token
      const authResult = await this.authRepository.checkAuth(request.token)
      
      if (!isOk(authResult)) {
        return createErr(new UnauthorizedError('Invalid token'))
      }

      const user = authResult.value

      // Create session
      const session: AuthSession = {
        user,
        accessToken: request.token,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours
      }

      // Store session
      const storeResult = await this.authRepository.storeSession(session)
      
      if (!isOk(storeResult)) {
        console.warn('Failed to store session:', storeResult.error)
        // Continue anyway, as authentication succeeded
      }

      return createOk({
        user,
        accessToken: request.token,
        expiresAt: session.expiresAt,
      })
    } catch (error) {
      console.error('LoginUseCase failed:', error)
      return createErr(error instanceof Error ? error : new Error('Login failed'))
    }
  }
}

export class LogoutUseCase implements IUseCase<LogoutUseCaseRequest, void> {
  constructor(private readonly authRepository: IAuthRepository) {}

  async execute(request: LogoutUseCaseRequest): Promise<Result<void, Error>> {
    try {
      const clearResult = await this.authRepository.clearSession()
      
      if (!isOk(clearResult)) {
        return createErr(clearResult.error)
      }

      console.log('User logged out:', request.userId)
      return createOk(undefined)
    } catch (error) {
      console.error('LogoutUseCase failed:', error)
      return createErr(error instanceof Error ? error : new Error('Logout failed'))
    }
  }
}